/**
 * videoVisit.js — MEET-patient PA0 + PA1 (PROD-538): the decisions behind the recording-consent
 * question and the video-visit join. Pure module, no imports, so the gate
 * (scripts/check-join-consent.mjs) runs it in node exactly as the screens run it.
 *
 * THE CONTRACT (orella-api MEET-A, MEET_PLAN 2c + MEET_PLAN_UPDATE 2c):
 *   - At booking, the patient booking routes (send-to-doctor, complete-onboarding,
 *     POST /appointments) read `recordingConsent: 'accepted' | 'declined'` and `consentVersion`
 *     at the top level of the body. Absent = not recorded (Q-M4). A booking is never refused
 *     over this field.
 *   - Before an in-app join: POST /appointments/<id>/recording-consent with
 *     `{decision, consentVersion}` (S5). Only a Google Meet row takes it; a Zoom row, an
 *     in-person row or a cancelled one answers 409, so a Zoom visit is never asked.
 *   - Appointment outputs carry `recordingConsent: {decision, at}` (the newest record, S5/M6).
 *
 * ★★ A visit link is opened only after the decision is SAVED. A failed POST opens nothing
 *   (the patient reads "We could not save your choice" and tries again).
 * ★★ Recording is OFF unless an `accepted` record exists, so not asking (Spanish until
 *   Dr. Rami approves the text, a Zoom row) means an unrecorded visit, never a recorded one.
 * ★★★ MEET P1 (PROD-671 not approved yet): MEET_RECORDING_RELEASED = false, so the question is never
 *   drawn in any language, nothing is sent at booking or join, and every Meet visit opens directly,
 *   unrecorded. The Join label and the Meet hint are not gated by it.
 */
import { wallTimeToInstant } from './dateUtils.js'

// ★ PLACEHOLDER (not fixed by the plan; listed for Alex in meet_pa/PROGRESS.md). The version
//   names the consent TEXT the patient saw; the English is a draft until counsel answers R-M2.
//   It must match the API's _CONSENT_VERSION_RE (^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$). Change it
//   the day the approved wording replaces the draft, so the records say which text was agreed to.
export const RECORDING_CONSENT_VERSION = 'meet-consent-en-draft-1'

export const RECORDING_CONSENT_DECISIONS = Object.freeze(['accepted', 'declined'])

// Every string the question draws. It is asked only when the UI language has approved text for
// ALL of them (src/i18n/pendingEs.js withholds them under es until Dr. Rami approves).
export const RECORDING_CONSENT_KEYS = Object.freeze([
  'videoVisit.consentTitle',
  'videoVisit.consentBody',
  'videoVisit.bookingAgree',
  'videoVisit.bookingDecline',
  // PA1, the pre-join sheet (JoinVideoVisit)
  'videoVisit.agreeAndJoin',
  'videoVisit.joinWithoutRecording',
  'videoVisit.notRecorded',
  'videoVisit.saveFailed',
  'visits.joinVideoVisit',
])

const VIRTUAL_TYPES = ['video', 'telehealth', 'virtual']

/** The API's _VIRTUAL_VISIT_TYPES ('video', 'telehealth', 'virtual'), any case. */
export function isVirtualVisitType(type) {
  return VIRTUAL_TYPES.includes(String(type || '').trim().toLowerCase())
}

// ★★★ PROD-671: the code key. false = the recording question is never drawn (booking or join), so every
//   Google Meet visit opens directly and is unrecorded. Flip to true ONLY in the reviewed release commit that
//   ships the approved consent text and renames RECORDING_CONSENT_VERSION, and only after the API's
//   _MEET_VISIT_RECORDING_RELEASED is True and live (scripts/check-join-consent.mjs K3).
//   A literal, never an environment value: turning recording on is a reviewed commit (K1).
export const MEET_RECORDING_RELEASED = false

/**
 * true when the question may be drawn: recording is released (MEET_RECORDING_RELEASED, read FIRST)
 * and every one of its strings has approved text. The booking question (useBookingRecordingConsent)
 * and the pre-join sheet (JoinVideoVisit) both ask only through this function.
 */
export function canAskRecordingConsent(hasText) {
  if (MEET_RECORDING_RELEASED !== true) return false
  return typeof hasText === 'function' && RECORDING_CONSENT_KEYS.every((k) => hasText(k))
}

/**
 * The API's word on an appointment row: `recordingAvailable: true` only while the API's
 * _MEET_VISIT_RECORDING_RELEASED is True (MEET A1). An older API sends no key, and an absent or
 * non-true key counts as NOT available, so the pre-join sheet is not drawn (not asked = not recorded).
 */
export function recordingAvailableOn(appt) {
  if (!appt || typeof appt !== 'object') return false
  return (appt.recordingAvailable ?? appt.recording_available) === true
}

/**
 * PA0 — the booking step. 'ask' for a virtual visit when the question can be drawn, else 'skip'
 * (an in-person visit is never asked; a skipped virtual visit is booked unrecorded).
 */
export function bookingConsentStep(visitType, canAsk) {
  return canAsk && isVirtualVisitType(visitType) ? 'ask' : 'skip'
}

/** The body fields a booking carries for a decision; {} for no decision. */
export function bookingConsentFields(decision) {
  if (!RECORDING_CONSENT_DECISIONS.includes(decision)) return {}
  return { recordingConsent: decision, consentVersion: RECORDING_CONSENT_VERSION }
}

/** PA2/PA3 — the i18n key of the Join button's label (JoinVideoVisit and a no-link stand-in share
 *  it): "Join video visit" when the language has approved text for it, else the approved,
 *  provider-neutral diagnose.joinVideoCall (es "Unirse a la videollamada"). PA3 retired
 *  visits.joinZoomMeeting: a Meet visit is never labelled Zoom. */
export const JOIN_VIDEO_VISIT_FALLBACK_KEY = 'diagnose.joinVideoCall'
export function joinVideoVisitLabelKey(hasText) {
  return typeof hasText === 'function' && hasText('visits.joinVideoVisit') ? 'visits.joinVideoVisit' : JOIN_VIDEO_VISIT_FALLBACK_KEY
}

/** PA3 — the hint under a Google Meet visit's Join button (MEET_PLAN 3.7, F-M8 / PROD-674). */
export const MEET_HINT_KEY = 'videoVisit.opensInGoogleMeet'

/** PA3 — true when the hint is drawn: a Google Meet visit, in a language with approved text. */
export function showMeetHint(appt, hasText) {
  return isMeetVisit(appt) && typeof hasText === 'function' && hasText(MEET_HINT_KEY)
}

/** PROD-1108 — the hint above a Meet Join button on iPhone/iPad Safari (en text approved by Alex 2026-10-17). */
export const IOS_MEET_HINT_KEY = 'videoVisit.iosOpenApp'

/**
 * PROD-1108 — true on iOS Safari-class browsers: iPhone / iPod / iPad UA, or iPadOS in desktop mode
 * (reports platform 'MacIntel' with touch points). Chrome on iPhone (CriOS) joins Meet in the browser
 * (measured by Alex 2026-10-17), so it is excluded. `env` = { userAgent, platform, maxTouchPoints }.
 */
export function isIosWebBrowser(env) {
  const e = env && typeof env === 'object' ? env : {}
  const ua = String(e.userAgent || '')
  if (/CriOS/.test(ua)) return false
  if (/iPhone|iPod|iPad/.test(ua)) return true
  return e.platform === 'MacIntel' && Number(e.maxTouchPoints) > 1
}

/** PROD-1108 — true when the iOS "tap Open app" hint is drawn: a Meet visit, an iOS web browser,
 *  NOT the native app shell (isNative; the native Meet behaviour is Dr. Rami's PROD-1000), and
 *  approved text in the language (withheld under es until Dr. Rami approves). */
export function showIosMeetHint(appt, hasText, env, isNative) {
  return !isNative && isMeetVisit(appt) && isIosWebBrowser(env)
    && typeof hasText === 'function' && hasText(IOS_MEET_HINT_KEY)
}

/**
 * PROD-1011 (Alex Q67) — a Join control exists only for a CONFIRMED visit. 'confirmed' and
 * 'scheduled' are the two statuses of a booked visit; a pending request, a held 'pending_reschedule'
 * row, a cancelled / completed row never joins, even with a meeting link present. No new copy.
 */
export const JOINABLE_VISIT_STATUSES = ['confirmed', 'scheduled']
export function isJoinableVisitStatus(status) {
  return JOINABLE_VISIT_STATUSES.includes(typeof status === 'string' ? status.trim().toLowerCase() : status)
}

/** The visit link of an appointment object, in the order the API writes it (meeting_url first). */
export function visitMeetingUrl(appt) {
  if (!appt || typeof appt !== 'object') return null
  const url = appt.meeting_url || appt.meetingUrl || appt.meetingLink || appt.meeting_link
    || appt.zoom_link || appt.zoomLink || appt.video_link || appt.videoLink
  return typeof url === 'string' && /^https:\/\//i.test(url.trim()) ? url.trim() : null
}

/**
 * PA2 — what a join point hands JoinVideoVisit: { id, meeting_url, meeting_provider,
 * recordingConsent, recordingAvailable }, or null when there is no https link. `row` is the appointment ROW (the
 * API's snake_case or a screen's camelCase mapping of it); `fallback` an object that carries only
 * a link (the case's appointmentRequest JSONB). The link is visitMeetingUrl's (meeting_url first).
 * ★ The id, the provider and the stored decision come from the row ONLY: a fallback's id is not an
 *   appointment id the consent route knows. Without an id JoinVideoVisit cannot save a decision,
 *   so it does not ask and opens the link (not asked = not recorded).
 */
export function videoJoinTarget(row, fallback = null) {
  const r = row && typeof row === 'object' ? row : null
  // ★ PROD-1011: the row's own status decides (else the fallback's); a row with a status that is not
  //   confirmed / scheduled has no join target. A row with no status at all keeps the Visits
  //   convention (no status = confirmed), so a legacy row without the field still joins.
  const status = r?.status || (fallback && typeof fallback === 'object' ? fallback.status : null)
  if (status && !isJoinableVisitStatus(status)) return null
  const url = visitMeetingUrl(r) || visitMeetingUrl(fallback)
  if (!url) return null
  // s126c: the visit's own date + time (the row first, else the fallback), for the 15-minute join window
  const slot = visitSlotOf(r) || visitSlotOf(fallback)
  return {
    id: r?.id || null,
    ...(slot ? { date: slot.date, time: slot.time } : {}),
    meeting_url: url,
    meeting_provider: r?.meeting_provider || r?.meetingProvider || null,
    recordingConsent: r?.recordingConsent || r?.recording_consent || null,
    // MEET P1: the API's recordingAvailable, from the row ONLY (absent = false = not asked)
    recordingAvailable: recordingAvailableOn(r),
  }
}

/**
 * PA6 (R3 F7) — the appointment id the recording-consent route can take, or null.
 * The route looks the id up in a uuid column and answers 404 for anything else. GET /appointments
 * also lists SYNTHESIZED rows ('initial-<case id>' / 'followup-<case id>') that are not appointment
 * rows; their link can be a Meet link and they carry no provider. ★ An id that is not a uuid
 * counts as NO id: no decision can be saved for it, so JoinVideoVisit does not ask and opens the
 * link (not asked = not recorded), instead of a sheet whose POST can only fail.
 * Only the canonical 8-4-4-4-12 shape (any case), the one the API accepts.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export function isUuid(value) {
  return typeof value === 'string' && UUID_SHAPE.test(value)
}
export function consentAppointmentId(appt, appointmentId = null) {
  const id = appointmentId || (appt && typeof appt === 'object' ? appt.id : null) || null
  return isUuid(id) ? id : null
}

/** A Google Meet visit: the row says so, or its link is a meet.google.com link. */
export function isMeetVisit(appt) {
  if (!appt || typeof appt !== 'object') return false
  const provider = appt.meeting_provider || appt.meetingProvider
  if (provider) return provider === 'google_meet'
  const url = visitMeetingUrl(appt)
  if (!url) return false
  try { return new URL(url).hostname.toLowerCase() === 'meet.google.com' } catch { return false }
}

/** The newest stored decision ('accepted' | 'declined') or null (never answered). */
export function storedConsentDecision(appt) {
  const rc = appt && typeof appt === 'object' ? (appt.recordingConsent || appt.recording_consent) : null
  const d = rc && typeof rc === 'object' ? rc.decision : rc
  return RECORDING_CONSENT_DECISIONS.includes(d) ? d : null
}

/**
 * PA1 — what a tap on Join does: 'none' (no link), 'ask' (the pre-join sheet) or 'open'.
 * Asked: a Google Meet visit whose newest record is absent or 'declined' (MEET_PLAN_UPDATE PA1),
 * when the question can be drawn. Opened directly: an 'accepted' record, a decision made in this
 * sheet already (`decidedNow`), a Zoom row (nothing to consent to; the API refuses its POST),
 * a language without approved text, recording not released (canAsk false: MEET_RECORDING_RELEASED),
 * or a row the API does not mark `recordingAvailable: true` (MEET P1; all unrecorded).
 */
export function joinPlan(appt, { canAsk = false, decidedNow = null } = {}) {
  if (!visitMeetingUrl(appt)) return 'none'
  if (RECORDING_CONSENT_DECISIONS.includes(decidedNow)) return 'open'
  if (!canAsk || !isMeetVisit(appt) || !recordingAvailableOn(appt)) return 'open'
  return storedConsentDecision(appt) === 'accepted' ? 'open' : 'ask'
}

/**
 * PA1 — the sheet's two buttons: save the decision, THEN open. -> { saved, opened }.
 * ★★ postConsent throws (a refusal, a network error, a timeout) -> { saved: false, opened: false }
 *   and openUrl is NOT called: a failed POST opens nothing.
 * openUrl may answer false (a browser that blocked the window): the caller then offers the link
 * as a tap of its own.
 */
export async function submitConsentThenOpen({ appointmentId, url, decision, postConsent, openUrl }) {
  // PA6: a non-uuid id (a synthesized 'followup-<case>' row) is no id; the POST could only 404.
  if (!isUuid(appointmentId) || !url || !RECORDING_CONSENT_DECISIONS.includes(decision)) return { saved: false, opened: false }
  try {
    await postConsent(appointmentId, { decision, consentVersion: RECORDING_CONSENT_VERSION })
  } catch {
    return { saved: false, opened: false }
  }
  let opened = false
  try { opened = (await openUrl(url)) !== false } catch { opened = false }
  return { saved: true, opened }
}

/**
 * s126c (Dr. Rami 2026-11-01, TestFlight): "Join meeting should open to join 15min before the appointment time".
 * The visit's date + time as the screens hold them (device-local, the same reading appointmentWindow.js gives the
 * check-in window). One object at a time, so a date is never paired with another object's time.
 */
export const JOIN_OPENS_MINUTES_BEFORE = 15
const DATE_KEYS = ['date', 'scheduled_date', 'scheduledDate', 'appointment_date', 'appointmentDate', 'confirmedDate', 'requestedDate']
const TIME_KEYS = ['time', 'scheduled_time', 'scheduledTime', 'appointment_time', 'appointmentTime', 'confirmedTime', 'requestedTime']
export function visitSlotOf(obj) {
  if (!obj || typeof obj !== 'object') return null
  const date = DATE_KEYS.map((k) => obj[k]).find((v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v))
  const time = TIME_KEYS.map((k) => obj[k]).find((v) => typeof v === 'string' && v.trim())
  return date && time ? { date: date.slice(0, 10), time: time.trim() } : null
}

/** The visit's start as a Date, or null when the target carries no readable date + time. '14:30', '14:30:00', '2:30 PM'. */
export function visitStartOf(target) {
  const slot = visitSlotOf(target)
  if (!slot) return null
  const [y, mo, d] = slot.date.split('-').map(Number)
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp]\.?\s?[Mm]\.?)?$/.exec(slot.time)
  if (!m) return null
  let h = Number(m[1])
  const min = Number(m[2])
  if (m[3]) {
    const pm = /^[Pp]/.test(m[3])
    if (h === 12) h = pm ? 12 : 0
    else if (pm) h += 12
  }
  if (h > 23 || min > 59) return null
  // The slot is the practice's (Los Angeles) wall clock, never the device's: wallTimeToInstant makes it the true instant.
  const dt = wallTimeToInstant(slot.date, `${h}:${String(min).padStart(2, '0')}`)
  return dt && !Number.isNaN(dt.getTime()) ? dt : null
}

/** When Join opens (15 minutes before the start), or null (no readable start: Join is not held). */
export function joinOpensAt(target) {
  const start = visitStartOf(target)
  return start ? new Date(start.getTime() - JOIN_OPENS_MINUTES_BEFORE * 60000) : null
}

/** true when Join may be tapped now. ★ No readable start = open (a link is never locked away by a missing time). */
export function joinIsOpen(target, now = Date.now()) {
  const at = joinOpensAt(target)
  return !at || Number(now) >= at.getTime()
}

/**
 * s126c (Dr. Rami 2026-11-01): the video sign-on (identity, location, the telemedicine consent) is asked when the
 * patient taps Join, not during check-in, and the API stores it only after check-in. So a Join outside the
 * check-in screen goes through the check-in first: Visits opens CheckInModal with join intent (already checked in =
 * straight to the sign-on, then the visit). The path a screen without its own check-in navigates to.
 */
export function joinCheckInPath(caseId, visitId) {
  const q = new URLSearchParams({ openCheckIn: '1', join: '1' })
  if (caseId) q.set('caseId', String(caseId))
  if (visitId) q.set('visitId', String(visitId))
  return `/visits?${q.toString()}`
}
